import React, { useState } from 'react';
import {
  Label, Column, Row, Icon, Page,
} from 'src/components/Layout';
import { Form } from '@rocketseat/unform';
import Button from 'src/components/Button';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { authActions } from 'src/store/auth/auth.actions';
import styled from 'styled-components';
import { faSquare } from '@fortawesome/free-regular-svg-icons';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { LabeledInput } from 'src/components/LabeledInput';
import { getChangePassword } from 'src/store/auth/auth.selectors';
import { Redirect } from 'react-router-dom';
import { Header } from 'src/components/Header';
import { withRouter } from 'react-router';
import { motion } from 'framer-motion';
import { useToast } from 'src/components/Toast';
import { getBrandingColor, colorTypes } from 'src/branding';
import { routes } from '../routes.manifest';

const changingPasswordVariants = {
  hidden: { maxHeight: 0, overflow: 'hidden' },
  expanded: {
    marginBottom: '10px',
    maxHeight: '33px',
    transition: { duration: 0.3 },
  },
};

const ResetPasswordContentWrapper = styled(Column)`
  margin-top: 40px;
  margin-left: auto;
  margin-right: auto;
  width: 28%;

  @media screen and (max-width: 1100px) {
    width: 40%;
  }

  @media screen and (max-width: 800px) {
    width: 60%;
  }

  @media screen and (max-width: 500px) {
    width: 100%;
  }
`;

const ChangePasswordHeader = styled(Header)`
  @media screen and (min-width: 700px) {
    text-align: center;
  }
`;

const ResetPasswordFormContent = styled(Column)`
  width: 100%;
`;

export const Completed = ({ ...props }) => (
  <span style={{ marginRight: '6px', position: 'relative', ...props.style || {} }}>
    <Icon icon={faSquare} size="1.2rem" aria-hidden="true" />
    <CompletedCheck
      icon={faCheck}
      size="1.2rem"
      color="green"
      title="Oppnådd"
    />
  </span>
);

export const NotCompleted = () => (
  <span style={{ marginRight: '7px', position: 'relative' }}>
    <Icon icon={faSquare} size="1.2rem" title="Ikke oppnådd" />
  </span>
);

export const ErrorCompleted = () => (
  <span style={{ marginRight: '7px', position: 'relative' }}>
    <Icon icon={faTimes} size="1.2rem" title="Ikke oppnådd" color="gray" />
  </span>
);
const CompletedCheck = styled(Icon)`
  position: absolute;
  right: -4px;
  bottom: 2px;
  font-size: 1.1rem;
`;
export const ChangePassword = withRouter(({ history, isResetPassword }) => {
  const toast = useToast();
  const changePasswordStatus = useSelector(getChangePassword);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(
    false,
  );
  const dispatch = useDispatch();
  const PASS_MIN_LENGTH = 9;
  const [submittedPassword, setSubmittedPassword] = useState(false);

  const onSubmit = (form, { resetForm }) => {
    if (everyPasswordRuleAchieved) {
      resetForm();
      setPasswordRulesAchieved({
        len: false,
        containsNumber: false,
        containsSpecialOrUppercase: false,
        newPasswordsMatching: false,
      });

      setShowPasswordRequirements(false);
      setSubmittedPassword(true);

      if (isResetPassword) {
        const params = new URLSearchParams(window.location.search);
        dispatch(
          authActions.changePassword({
            kind: 'RESET',
            new_password: form.newPassword,
            onSuccess: () => {
              toast.addMessage('Passordet ble endret');
            },
            onError: () => {
              toast.addError('Kunne ikke endre passordet');
            },
            t: params.get('t'),
          }),
        );
      } else {
        dispatch(
          authActions.changePassword({
            kind: 'CHANGE',
            password: form.oldPassword,
            new_password: form.newPassword,
          }),
        );
      }
    }
  };

  const [passwordRulesAchieved, setPasswordRulesAchieved] = useState({
    len: false,
    containsNumber: false,
    containsSpecialOrUppercase: false,
    newPasswordsMatching: false,
  });

  const everyPasswordRuleAchieved = Object.values(passwordRulesAchieved).every(
    (e) => e,
  );
  const [newPassword, setNewPassword] = useState(null);
  const [newPasswordRepeat, setNewPasswordRepeat] = useState(null);

  const formSchemaChange = Yup.object().shape({
    oldPassword: Yup.string().required('Feltet må fylles ut'),
    newPassword: Yup.string().required('Feltet må fylles ut'),
    newPasswordRepeat: Yup.string().required('Feltet må fylles ut'),
  });

  const formSchemaReset = Yup.object().shape({
    newPassword: Yup.string().required('Feltet må fylles ut'),
    newPasswordRepeat: Yup.string().required('Feltet må fylles ut'),
  });

  const PasswordReqLabel = styled(Label)`
    margin-top: 11px;
    display: flex;
  `;

  const navigateBack = () => {
    history.goBack();
  };

  return (
    <Page title="Endre passord" style={{ maxWidth: '1200px' }}>
      <ChangePasswordHeader style={{ marginBottom: '0em' }}>
        Endre profil
      </ChangePasswordHeader>
      <ResetPasswordContentWrapper>
        {changePasswordStatus.success && isResetPassword && (
          <Redirect to={routes.login} />
        )}
        {(isResetPassword && <Label as="h1">Sett nytt passord</Label>) || (
          <>
            <Label
              as="h2"
              size="1.22rem"
              color={getBrandingColor(colorTypes.fontHeader)}
              marginBottom="1em"
            >
              Endre passord:
            </Label>
          </>
        )}
        <Form
          onSubmit={onSubmit}
          schema={isResetPassword ? formSchemaReset : formSchemaChange}
        >
          <ResetPasswordFormContent>
            {!isResetPassword && (
              <LabeledInput
                label="Nåværende passord"
                name="oldPassword"
                type="password"
              />
            )}
            <LabeledInput
              label="Nytt passord"
              name="newPassword"
              type="password"
              onChange={(evt) => {
                const pass = evt.target.value;
                setNewPassword(pass);

                const isPasswordLongEnough = pass.length >= PASS_MIN_LENGTH;
                const hasNumbers = /[0-9]/.test(pass);
                const hasSpecial = /[^A-Za-z0-9]/.test(pass);
                const hasUpper = /[A-Z]/.test(pass);

                setPasswordRulesAchieved({
                  ...passwordRulesAchieved,
                  len: isPasswordLongEnough,
                  containsNumber: hasNumbers,
                  containsSpecialOrUppercase: hasSpecial || hasUpper,
                  newPasswordsMatching:
                    newPasswordRepeat === pass && newPasswordRepeat !== '',
                });

                if (!showPasswordRequirements) {
                  setShowPasswordRequirements(true);
                }
              }}
            />

            <LabeledInput
              label="Gjenta nytt passord"
              name="newPasswordRepeat"
              style={{ marginBottom: '1.85em' }}
              type="password"
              onChange={(evt) => {
                const passRepeat = evt.target.value;
                setNewPasswordRepeat(passRepeat);
                setPasswordRulesAchieved({
                  ...passwordRulesAchieved,
                  newPasswordsMatching: passRepeat === newPassword,
                });

                if (!showPasswordRequirements) {
                  setShowPasswordRequirements(true);
                }
              }}
            />

            {showPasswordRequirements && (
              <>
                <Label>Krav for passord:</Label>
                <PasswordReqLabel>
                  {(passwordRulesAchieved.len && <Completed />) || (
                    <NotCompleted />
                  )}
                  Minst 9 tegn langt
                </PasswordReqLabel>
                <PasswordReqLabel>
                  {(passwordRulesAchieved.containsNumber && (
                    <Completed />
                  )) || <NotCompleted />}
                  Minst ett tall
                </PasswordReqLabel>
                <PasswordReqLabel>
                  {(passwordRulesAchieved.containsSpecialOrUppercase && (
                    <Completed />
                  )) || <NotCompleted />}
                  {' '}
                  Minst ett spesialtegn eller stor bokstav
                </PasswordReqLabel>
                <PasswordReqLabel>
                  {(passwordRulesAchieved.newPasswordsMatching && (
                    <Completed />
                  )) || <NotCompleted />}
                  {' '}
                  De nye passordene samsvarer
                </PasswordReqLabel>
              </>
            )}
            <motion.div
              variants={changingPasswordVariants}
              initial="hidden"
              animate={changePasswordStatus.isFetching ? 'expanded' : 'hidden'}
            >
              <p>Endrer passord ...</p>
            </motion.div>

            {changePasswordStatus.success && submittedPassword && (
              <p>Passordet ble endret</p>
            )}

            <motion.div
              initial="hidden"
              variants={{
                ...changingPasswordVariants,
                expanded: {
                  ...changingPasswordVariants.expanded,
                  maxHeight: '80px',
                },
              }}
              animate={changePasswordStatus.error ? 'expanded' : 'hidden'}
            >
              <p>
                Kunne ikke endre passordet. Muligens er ikke Nåværende passord
                korrekt
                {' '}
                {isResetPassword ? 'eller lenken er ikke lenger gyldig' : ''}
                .
              </p>
            </motion.div>

            <Row marginTop="2.5em">
              <Button type="button" secondary onClick={navigateBack}>
                {changePasswordStatus.success ? 'Gå tilbake' : 'Avbryt'}
              </Button>
              <span
                style={{ marginRight: '10px', display: 'inline-block' }}
              />
              <Button
                type="submit"
                primary
                disabled={!everyPasswordRuleAchieved}
              >
                Lagre passord
              </Button>
            </Row>
          </ResetPasswordFormContent>
        </Form>
      </ResetPasswordContentWrapper>
    </Page>
  );
});
